import { buildPlayerTeamWorkspacePath, readRememberedPlayerTeamWorkspace } from './playerTeamWorkspace';

const DEEP_LINK_KEYS = ['open_match_chat', 'open_match_schedule', 'open_match_score', 'open_match_details'];

/**
 * Preserve match deep-link query params when rebuilding team workspace URLs.
 * @param {URLSearchParams} searchParams
 * @returns {Record<string, string>}
 */
export function collectPlayerTeamWorkspaceDeepLinkParams(searchParams) {
  const out = {};
  if (!searchParams) return out;
  DEEP_LINK_KEYS.forEach((k) => {
    const v = searchParams.get(k);
    if (v) out[k] = v;
  });
  return out;
}

/**
 * @param {object} n notification row from API
 * @returns {string} nav screen key for buildLeagueMatchDeepLinkQuery
 */
export function deriveNotificationNavScreen(n) {
  const raw = n?.navScreen != null ? String(n.navScreen).trim() : '';
  if (raw === 'match_schedule') return 'match_schedule';
  if (raw === 'league_score') return 'league_score';
  if (raw === 'league_match_details') return 'league_match_details';
  if (raw === 'match_chat') return 'match_chat';

  const type = String(n?.type || '');
  if (type.startsWith('league_score')) return 'league_score';
  const title = String(n?.title || '').toLowerCase();
  if (type === 'match_chat_message' && title.includes('match schedule')) return 'match_schedule';
  if (type === 'match_chat_message') return 'match_chat';
  if (n?.leagueMatchId && type.includes('scheduling')) return 'match_schedule';
  return 'match_chat';
}

/**
 * @param {string} leagueMatchId
 * @param {string|null} navScreen from deriveNotificationNavScreen
 * @returns {Record<string, string>}
 */
export function buildLeagueMatchDeepLinkQuery(leagueMatchId, navScreen) {
  const id = String(leagueMatchId);
  switch (navScreen) {
    case 'match_schedule':
      return { open_match_schedule: id };
    case 'league_score':
      return { open_match_score: id };
    case 'league_match_details':
      return { open_match_details: id };
    default:
      return { open_match_chat: id };
  }
}

export function resolveTeamWorkspaceQueryParams(teamId, rosterTeams) {
  const fromRoster = (rosterTeams || []).find((t) => String(t.teamId || t.team_id) === String(teamId));
  if (fromRoster?.tenantId && fromRoster?.latestSeasonId && fromRoster?.campaignId) {
    return {
      tenant_id: String(fromRoster.tenantId),
      season_id: String(fromRoster.latestSeasonId),
      campaign_id: String(fromRoster.campaignId),
    };
  }
  const mem = readRememberedPlayerTeamWorkspace(teamId);
  if (mem?.tenant_id && mem?.season_id && mem?.campaign_id) {
    return {
      tenant_id: mem.tenant_id,
      season_id: mem.season_id,
      campaign_id: mem.campaign_id,
    };
  }
  return null;
}

/**
 * @param {object} n
 * @param {object} ctx
 * @param {function(string): void} ctx.navigate
 * @param {function(string): void} [ctx.navigateIfProfileAllows]
 * @param {Array} [ctx.rosterTeams]
 * @param {() => Promise<{ data?: unknown }>} ctx.playerAPIGetTeams
 * @param {import('react-query').QueryClient} [ctx.queryClient]
 */
export async function navigateFromPlayerNotification(n, ctx) {
  const nav = ctx.navigateIfProfileAllows || ctx.navigate;
  if (!n || !nav) return;

  if (n.type === 'team_disabled') {
    ctx.queryClient?.invalidateQueries(['playerTeams']);
    nav('/player/teams');
    return;
  }

  if (n.type === 'removed_from_team') {
    nav('/player/teams');
    return;
  }

  if ((n.type === 'unread_message' || n.type === 'unread_message_reminder') && n.conversationId) {
    nav(`/player/messages/${n.conversationId}`);
    return;
  }

  if (n.type === 'match_found') {
    nav('/player/find-match');
    return;
  }

  if (n.type === 'match_accepted' || n.type === 'score_submitted') {
    if (n.matchId) {
      nav(`/player/matches/${n.matchId}`);
    } else {
      nav('/player/find-match');
    }
    return;
  }

  if (n.type === 'team_join_request' || n.type === 'team_invitation') {
    nav('/player/teams?tab=requests');
    return;
  }

  if (n.type === 'home_banner') {
    const path =
      (n.ctaPath && String(n.ctaPath).startsWith('/player')) || n.ctaPath === '/player'
        ? n.ctaPath
        : n.navScreen && (String(n.navScreen).startsWith('/player') || n.navScreen === '/player')
          ? n.navScreen
          : '/player';
    nav(path);
    return;
  }

  const leagueTypes = new Set([
    'match_chat_message',
    'league_score_submitted',
    'league_score_resubmitted',
    'league_score_disputed',
    'league_score_confirmed',
    'match_scheduling_round_open',
    'availability_slot_vote',
  ]);
  const isLeague =
    (n.leagueMatchId && (leagueTypes.has(n.type) || String(n.type || '').startsWith('league_'))) ||
    (n.leagueMatchId && n.teamId);

  if (isLeague && n.leagueMatchId && n.teamId) {
    let qs = resolveTeamWorkspaceQueryParams(n.teamId, ctx.rosterTeams);
    if (!qs && ctx.playerAPIGetTeams) {
      try {
        const res = await ctx.playerAPIGetTeams();
        const list = Array.isArray(res?.data) ? res.data : res?.data?.data ?? [];
        qs = resolveTeamWorkspaceQueryParams(n.teamId, list);
      } catch {
        qs = null;
      }
    }
    if (!qs) {
      nav('/player/teams');
      return;
    }
    const screen = deriveNotificationNavScreen(n);
    const deep = buildLeagueMatchDeepLinkQuery(n.leagueMatchId, screen);
    nav(buildPlayerTeamWorkspacePath(n.teamId, 'overview', { ...qs, ...deep }));
    return;
  }

  if (n.leagueMatchId && n.teamId) {
    const qs = resolveTeamWorkspaceQueryParams(n.teamId, ctx.rosterTeams);
    if (qs) {
      const screen = deriveNotificationNavScreen(n);
      const deep = buildLeagueMatchDeepLinkQuery(n.leagueMatchId, screen);
      nav(buildPlayerTeamWorkspacePath(n.teamId, 'overview', { ...qs, ...deep }));
      return;
    }
  }

  nav('/player/notifications');
}
